import { useSyncExternalStore } from "react";
import { apiFetch, errorMessage } from "../../redux/api";
import { selectSessionId } from "../../redux/reducer/app-slice";
import { store } from "../../redux/store";
import {
  detectPushSupport,
  findSubscription,
  notificationPermission,
  pushSupport,
  requestNotificationPermission,
  subscribePush,
  toSubscriptionPayload,
  unsubscribePush,
} from "../../utils/push-subscription";

let state = {
  support: detectPushSupport(),
  permission: notificationPermission(),
  key: null,
  endpoint: null,
  checked: false,
};
const listeners = new Set();

const setState = (change) => {
  state = { ...state, ...change };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => state;

const session = () => selectSessionId(store.getState());

const send = (path, options) => apiFetch(session(), path, options);

let keyRequest = null;

const loadKey = () => {
  if (state.key) return Promise.resolve(state.key);
  keyRequest ??= send("/push/key")
    .then((key) => {
      setState({ key });
      return key;
    })
    .finally(() => {
      keyRequest = null;
    });
  return keyRequest;
};

const saveSubscription = (subscription) =>
  send("/push/subscription", {
    method: "POST",
    body: { subscription: toSubscriptionPayload(subscription) },
  });

export const refreshPushState = async () => {
  const support = detectPushSupport();
  setState({ support, permission: notificationPermission() });
  if (support !== pushSupport.supported) {
    setState({ checked: true });
    return state;
  }
  const [subscription] = await Promise.all([
    findSubscription().catch(() => null),
    loadKey().catch(() => null),
  ]);
  setState({ endpoint: subscription?.endpoint ?? null, checked: true });
  return state;
};

export const syncPushSubscription = async () => {
  if (detectPushSupport() !== pushSupport.supported) return;
  if (notificationPermission() !== "granted" || !session()) return;
  const subscription = await findSubscription();
  setState({ endpoint: subscription?.endpoint ?? null });
  if (subscription) await saveSubscription(subscription);
};

export const enablePushFailedText =
  "Benachrichtigungen konnten nicht aktiviert werden.";

// Must run straight from a tap: iOS only shows the permission prompt for a user gesture.
export const enablePush = async () => {
  const permissionRequest =
    notificationPermission() === "granted"
      ? Promise.resolve("granted")
      : requestNotificationPermission();
  try {
    const permission = await permissionRequest;
    setState({ permission });
    if (permission !== "granted") return { permission };
    const key = await loadKey();
    if (!key?.enabled) return { permission, error: enablePushFailedText };
    const subscription = await subscribePush(key.publicKey);
    if (!subscription) return { permission, error: enablePushFailedText };
    await saveSubscription(subscription);
    setState({ endpoint: subscription.endpoint });
    return { permission, subscribed: true };
  } catch (error) {
    return {
      permission: notificationPermission(),
      error: errorMessage(error, enablePushFailedText),
    };
  }
};

export const disablePushOnDevice = async () => {
  try {
    const endpoint = await unsubscribePush();
    setState({ endpoint: null });
    if (endpoint) {
      await send("/push/subscription", { method: "DELETE", body: { endpoint } });
    }
    return {};
  } catch (error) {
    return {
      error: errorMessage(
        error,
        "Benachrichtigungen konnten nicht ausgeschaltet werden.",
      ),
    };
  }
};

export const usePushState = () => useSyncExternalStore(subscribe, getSnapshot);
